import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ensureApiAuthenticated, getFrontendAuthMode, getSessionToken, isSessionValid } from '../services/api';
import { listPendingManapoolOrders } from '../api/manapoolOrders';

const CACHE_KEY = 'manapool_pending_count';
const POLL_INTERVAL = 60_000;
export const PENDING_ORDERS_CHANGED_EVENT = 'pending-orders:changed';

export const notifyPendingOrdersChanged = () => {
  window.dispatchEvent(new CustomEvent(PENDING_ORDERS_CHANGED_EVENT));
};

interface PendingOrdersContextValue {
  pendingCount: number;
  refresh: () => void;
}

const PendingOrdersContext = createContext<PendingOrdersContextValue>({
  pendingCount: 0,
  refresh: () => {},
});

export const PendingOrdersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pendingCount, setPendingCount] = useState<number>(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    const parsed = cached ? parseInt(cached, 10) : NaN;
    return isNaN(parsed) ? 0 : parsed;
  });

  const isMountedRef = useRef(true);

  const fetchCount = async () => {
    try {
      await ensureApiAuthenticated();

      // Avoid protected polling calls before sign-in in Entra/Hybrid flows.
      const authMode = getFrontendAuthMode();
      const hasValidSession = Boolean(getSessionToken()) && isSessionValid();
      if (!hasValidSession && authMode !== 'local') {
        return;
      }

      const response = await listPendingManapoolOrders(0, 1);
      if (isMountedRef.current) {
        setPendingCount(response.total);
        localStorage.setItem(CACHE_KEY, String(response.total));
      }
    } catch {
      // leave count unchanged on error
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchCount();

    const handlePendingOrdersChanged = () => {
      fetchCount();
    };

    window.addEventListener(PENDING_ORDERS_CHANGED_EVENT, handlePendingOrdersChanged);
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener(PENDING_ORDERS_CHANGED_EVENT, handlePendingOrdersChanged);
    };
  }, []);

  return (
    <PendingOrdersContext.Provider value={{ pendingCount, refresh: fetchCount }}>
      {children}
    </PendingOrdersContext.Provider>
  );
};

export const usePendingOrders = () => useContext(PendingOrdersContext);
